import { Table } from '@tanstack/react-table';
import { useState } from 'react';
import { CheckboxLabeled } from '../../../../../design-system/checkbox/checkbox-labeled';
import { Stack } from '../../../../../design-system/stack/stack';
import { NAME_COLUMN_ID } from './review-matches-table-columns-definition';
import { MatchFilter } from './utils/match-transaction-status-filter';

const MATCHED_FILTER_VALUE: Extract<MatchFilter, 'MATCHED'> = 'MATCHED';
const UNMATCHED_FILTER_VALUE: Extract<MatchFilter, 'UNMATCHED'> = 'UNMATCHED';
const ALL_FILTER_VALUE: Extract<MatchFilter, 'ALL'> = 'ALL';

type MatchedTransactionsFilterSectionProps<TData> = {
  table: Table<TData>;
};

export const MatchedTransactionsFilterSection = <TData,>({ table }: MatchedTransactionsFilterSectionProps<TData>) => {
  const { shouldCheckMatched, shouldCheckUnmatched } = getInitialMatchFilters(table);

  const [isMatchedChecked, setIsMatchedChecked] = useState(shouldCheckMatched);
  const [isUnmatchedChecked, setIsUnmatchedChecked] = useState(shouldCheckUnmatched);

  const applyFilter = (nextMatched: boolean, nextUnmatched: boolean) => {
    if (nextMatched) {
      if (!nextUnmatched) {
        table.getColumn(NAME_COLUMN_ID)?.setFilterValue(MATCHED_FILTER_VALUE);

        return;
      }

      if (nextUnmatched) {
        table.getColumn(NAME_COLUMN_ID)?.setFilterValue(ALL_FILTER_VALUE);

        return;
      }
    }

    if (!nextMatched) {
      if (nextUnmatched) {
        table.getColumn(NAME_COLUMN_ID)?.setFilterValue(UNMATCHED_FILTER_VALUE);

        return;
      }
    }

    table.getColumn(NAME_COLUMN_ID)?.setFilterValue(undefined);
  };

  return (
    <Stack space={1.5}>
      <h4 className="text-xs leading-5 font-medium tracking-tight text-muted-foreground">Matched transactions</h4>

      <Stack space={2}>
        <CheckboxLabeled
          label="Matched"
          checked={isMatchedChecked}
          onCheckedChange={(checked) => {
            const nextMatchedCheckValue = checked === true;

            setIsMatchedChecked(nextMatchedCheckValue);

            applyFilter(nextMatchedCheckValue, isUnmatchedChecked);
          }}
        />

        <CheckboxLabeled
          label="Unmatched"
          checked={isUnmatchedChecked}
          onCheckedChange={(checked) => {
            const nextUnmatchedFilterChecked = checked === true;

            setIsUnmatchedChecked(nextUnmatchedFilterChecked);

            applyFilter(isMatchedChecked, nextUnmatchedFilterChecked);
          }}
        />
      </Stack>
    </Stack>
  );
};

const getInitialMatchFilters = <TData,>(table: Table<TData>) => {
  const matchedTransactionFilter = table.getState().columnFilters.find((f) => f.id === NAME_COLUMN_ID);

  const matchFilterValue = matchedTransactionFilter?.value as MatchFilter | undefined;

  const shouldCheckMatched = matchFilterValue === MATCHED_FILTER_VALUE || matchFilterValue === ALL_FILTER_VALUE;
  const shouldCheckUnmatched = matchFilterValue === UNMATCHED_FILTER_VALUE || matchFilterValue === ALL_FILTER_VALUE;

  return { shouldCheckMatched, shouldCheckUnmatched };
};
